import { type CurvaDocument } from "@/document/model/document";
import {
  createEmptyScriptLibrary,
  type ScriptLibrary,
} from "@/document/model/documentScripts";
import {
  clearEditorScriptsDocument,
  createEmptyEditorScriptsDocument,
  EDITOR_SCRIPTS_DOCUMENT_NAME,
  loadEditorScriptsDocument,
  saveEditorScriptsDocument,
} from "@/io/editorScriptsPersistence";
import { StoreSubject } from "@/lib/store/StoreSubject";

function cloneDocument(document: CurvaDocument): CurvaDocument {
  return structuredClone(document);
}

function emptyLibrary(): ScriptLibrary {
  return createEmptyScriptLibrary();
}

/**
 * In-memory editor-owned scripts package (global across documents).
 * Persistence is async; the store is the sync source of truth for UI/commands.
 */
export class EditorScriptsStore {
  readonly document = new StoreSubject<CurvaDocument | undefined>(undefined);
  readonly library = new StoreSubject<ScriptLibrary>(emptyLibrary());
  private bootstrapped = false;
  private bootstrapPromise: Promise<void> | undefined;

  getDocument = (): CurvaDocument | undefined => {
    const current = this.document.getValue();
    return current ? cloneDocument(current) : undefined;
  };

  getLibrary = (): ScriptLibrary => {
    return structuredClone(this.library.getValue());
  };

  hasScripts = (): boolean => {
    return (this.library.getValue().items?.length ?? 0) > 0;
  };

  setDocument = (document: CurvaDocument | undefined): void => {
    if (!document) {
      this.document.next(undefined);
      this.library.next(emptyLibrary());
      return;
    }
    const next = cloneDocument(document);
    next.name = next.name || EDITOR_SCRIPTS_DOCUMENT_NAME;
    this.document.next(next);
    this.library.next(structuredClone(next.scripts ?? emptyLibrary()));
  };

  /** Apply live edits from the special editor-scripts workspace session. */
  syncFromLiveDocument = (document: CurvaDocument): void => {
    this.setDocument(document);
  };

  bootstrap = async (): Promise<void> => {
    if (this.bootstrapped) {
      return;
    }
    if (this.bootstrapPromise) {
      return this.bootstrapPromise;
    }

    this.bootstrapPromise = (async () => {
      try {
        const loaded = await loadEditorScriptsDocument();
        this.setDocument(loaded);
        this.bootstrapped = true;
      } catch (error) {
        console.warn("[curva] failed to load editor scripts", error);
        // Allow a later retry (e.g. after IndexedDB upgrade / tab unblocked).
        this.bootstrapPromise = undefined;
        throw error;
      }
    })();

    return this.bootstrapPromise;
  };

  /** Test helper: reset bootstrap gate without clearing in-memory state. */
  resetBootstrapForTests = (): void => {
    this.bootstrapped = false;
    this.bootstrapPromise = undefined;
  };

  persistCurrent = async (): Promise<void> => {
    const current = this.document.getValue();
    if (!current) {
      await clearEditorScriptsDocument();
      return;
    }
    await saveEditorScriptsDocument(current);
  };

  /** Ensure a document exists in memory (does not persist until save). */
  ensureDocument = (): CurvaDocument => {
    const current = this.getDocument();
    if (current) {
      return current;
    }
    const created = createEmptyEditorScriptsDocument();
    this.setDocument(created);
    return created;
  };
}

export const editorScriptsStore = new EditorScriptsStore();

/** Whether the Scripts panel should show the readonly Editor scripts section. */
export function shouldShowEditorScriptsPane(options: {
  editingEditorScripts: boolean;
  itemCount: number;
}): boolean {
  return !options.editingEditorScripts && options.itemCount > 0;
}
